import { connect } from '@client/decorators/common';
import { getShowById } from '@client/selectors/entities/shows';
import { createSelectorFactory } from '@client/selectors/lib';
import { startFetchShowDetails, unsubscribeShow, startFetchPublicShowDetails } from '@client/slices/entities/shows.reducer';
import { withData } from '@client/decorators/common/withData';
import { withShowProductsListeners } from '../withShowProducts/with-show-products.listeners';
import { withShowListeners } from './with-show.listeners';
import type { PublicShow } from '@sda/interfaces/show';
import type { RootState } from '@client/store';
import type { WithDataOptions } from '@client/decorators/common/withData';
import type { match as Match } from 'react-router';
import type { FetchDataType } from '@sda/sdk/interfaces';

const getShowByIdFactory = createSelectorFactory(getShowById);

interface WithShowOptions extends WithDataOptions {
  readonly fetchStripeSKU?: boolean;
}

/**
 * Decorate a component with a call to fetch an show by ID
 */
export const withShow = <TProps>(options?: WithShowOptions) => {
  const { fetchStripeSKU = true } = options || {};

  const mapStateToProps = () => {
    const getShowByIdSelector = getShowByIdFactory();

    return (state: RootState, { match, showId }: { match?: Match<{ showId: string }>, showId?: string }) => {
      const id = showId || match.params.showId;
      const show = getShowByIdSelector(state, id) as PublicShow & FetchDataType;

      return {
        show,
        showId: id,
        current: id,
        canAccess: !!(show && show.privateUpdatedAt),
      };
    };
  };

  const fetchMethod = fetchStripeSKU ? startFetchShowDetails : startFetchPublicShowDetails;

  const mapDispatchToProps = {
    fetch: fetchMethod,
    unfetch: unsubscribeShow,
  };

  return (
    WrappedComponent: React.ElementType<TProps & ReturnType<ReturnType<typeof mapStateToProps>>>,
  ) => connect(mapStateToProps, mapDispatchToProps)(withData(WrappedComponent, options, [withShowListeners, withShowProductsListeners]));
};
